import React, { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../helpers/store";
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { emailLogin, setLoginEmail } from "./loginSlice";
import { emailValidation } from "../../helpers/utils";
import { useHistory } from "react-router-dom";
import { ReducerStates, Views } from "../../helpers/constants";
import { LoginEmailDialog } from "../common/LoginEmailDialog";
import { RedirectErrors } from "../../services/constants";

// Component styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginFormContainer: {
      width: "480px",
      display: "flex",
      alignItems: "left",
      flexDirection: "column",
      [theme.breakpoints.down("sm")]: {
        width: "360px",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    textFieldUser: {
      marginTop: "32px",
    },
    helperText: {
      minHeight: "20px",
      lineHeight: "1.2",
      margin: theme.spacing(1, 0),
    },
    buttonRoot: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: "1.1rem !important",
    },
    loginButton: {
      width: "100%",
    },
    mainText: {
      color: theme.palette.dark.main,
      fontWeight: "bold",
      fontSize: "2rem",
      width: "100%",
      marginTop: "90px",
    },
    subText: {
      color: theme.palette.dark.main,
      fontSize: "1rem",
      width: "480px",
      marginTop: "25px",
      alignItems: "left",
      lineHeight: "1.6rem",
    },
  })
);

/**
 * Login form component
 */
export default function LoginEmailForm(props: IProps) {
  /* ---- Hooks ---- */
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  /* ---- State ---- */
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [open, setOpen] = React.useState(false);

  /* ---- Selectors ---- */

  const loginState: ReducerStates = useSelector(
    (state: IAppState) => state.loginState.state
  );

  const loginEmailSent: boolean = useSelector(
    (state: IAppState) => state.loginState.loginEmailSent
  );

  const dashboardError = useSelector(
    (state: IAppState) => state.dashboardState.errorCode
  );

  const dashboardState = useSelector(
    (state: IAppState) => state.dashboardState.state
  );

  /* ---- Methods ---- */
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setEmail(value);
    if (emailValidation(value)) {
      setSubmitError(t("invalid_email"));
    } else {
      setSubmitError("");
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    dispatch(setLoginEmail(email));
    dispatch(emailLogin(email));
  };

  const handleErrorClose = () => {
    setOpen(false);
  };

  /* ---- Utils ---- */
  const validateEmail = () => {
    if (email !== "") {
      if (emailValidation(email)) {
        setEmailError(t("invalid_email"));
      } else {
        setEmailError("");
      }
    }
  };

  // Manage view flow
  useEffect(() => {
    if (ReducerStates[loginState] === ReducerStates.SUCCEEDED) {
      if (loginEmailSent) {
        history.push(Views.LOGIN_CONFIRMATION);
      } else {
        history.push(Views.SUBSCRIPTION);
      }
    }
  }, [loginState, history, loginEmailSent]);

  useEffect(() => {
    if (dashboardState === ReducerStates.FAILED) {
      let redirectErrorDetected = false;
      Object.keys(RedirectErrors).forEach((key) => {
        if (
          RedirectErrors[key as keyof typeof RedirectErrors] === dashboardError
        ) {
          redirectErrorDetected = true;
          history.push(Views.ACCESS_EXPIRED);
        }
      });
      if (!redirectErrorDetected) {
        setOpen(true);
      }
    }
  }, [loginState, history, dashboardState, dashboardError]);

  const disabledLogin = email === "" || submitError !== "";

  return (
    <React.Fragment>
      <form
        id="login-form"
        noValidate
        className={classes.loginFormContainer}
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <Typography className={classes.mainText}>
          {t("login_maintext")}
        </Typography>
        <Typography className={classes.subText}>
          {t("login_subtext")}
        </Typography>
        <FormControl
          variant="outlined"
          fullWidth
          className={classes.textFieldUser}
          error={emailError !== ""}
        >
          <InputLabel htmlFor="email">{t("email_address")}</InputLabel>
          <OutlinedInput
            name="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={validateEmail}
            label={t("email_address")}
            autoComplete="off"
          />
          <FormHelperText
            id="component-error-text"
            className={classes.helperText}
          >
            {emailError}
          </FormHelperText>
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={disabledLogin}
          className={classes.loginButton}
          classes={{
            root: classes.buttonRoot,
          }}
        >
          {t("sign_in_title")}
        </Button>
        <LoginEmailDialog toggle={open} onClose={handleErrorClose} />
      </form>
    </React.Fragment>
  );
}

interface IProps {
  mode: string;
}
